import { useMemo } from 'react';
import { useGetListOrgsQuery } from '@/api';
import type { ListOrg } from '@/api/debug/listOrgs';
import { AccessControlAction } from '@/constants';
import { selectUserAboutData, useAppSelector } from '@/store';
import { useAccessControl } from './useAccessControl';

const NO_ORGS: ListOrg[] = [];

/** `debug/list-orgs` is global-admin only, so only fetch it for that role. Others get an empty list. */
export const useOrganisations = (): ListOrg[] => {
  const isGlobalAdmin = useAccessControl(AccessControlAction.GLOBAL_ADMIN_ONLY);
  // One cached request shared by every caller. A failed fetch just leaves the list empty.
  const { data: orgsData } = useGetListOrgsQuery(undefined, { skip: !isGlobalAdmin });

  return orgsData?.orgs ?? NO_ORGS;
};

/** Name of the user's organisation, or `null` when unknown (every non-global-admin). */
export const useOrganisationName = (): string | null => {
  const userAboutData = useAppSelector(selectUserAboutData);
  const orgs = useOrganisations();

  return useMemo(
    () => orgs.find((org) => org.orgId === userAboutData?.organisationId)?.name ?? null,
    [orgs, userAboutData?.organisationId],
  );
};
